import { Button, DataTable, ResultsCard, ResultsToolbar } from 'shared/components/ui';
import React, { useState, useEffect } from 'react';
import { AssignmentTurnedIn, Refresh } from '@mui/icons-material';

import { useVerificacionesOme } from '../hooks/useVerificacionesOme';
import { verificacionOmeService } from '../services/verificacionOmeService';
import { formatearFecha } from '../../../shared/utils/dateUtils';
import '../../../features/turnos/components/GrillaTurnos.css';
import './BandejaVerificacionOme.css';

/**
 * Bandeja "OME por verificar".
 *
 * Lista los indicadores de verificación de OME pendientes para que el
 * Personal de Recepción consulte el Portal de PAMI. Muestra teléfono,
 * número de afiliado, nombre, apellido, fecha de alta y una marca visible
 * cuando el número de afiliado no pudo extraerse de la credencial.
 *
 * Permite resolver cada verificación con los botones "OME OK" / "OME no OK",
 * que informan a SugiAPI si la OME quedó verificada o no.
 */
const BandejaVerificacionOme = () => {
  const { verificaciones, loading, error, refrescar } = useVerificacionesOme('pendiente');

  // Id de la fila que se está resolviendo (para deshabilitar sus botones).
  const [resolviendoId, setResolviendoId] = useState(null);
  // Mensaje de error de las acciones de resolución (claro para el usuario).
  const [errorResolucion, setErrorResolucion] = useState(null);
  // Tipos de estudio para elegir al verificar la OME (se cargan una vez).
  const [tiposEstudio, setTiposEstudio] = useState([]);
  // Tipo de estudio elegido por fila: { [idVerificacionOme]: idTipoEstudio }.
  const [estudioPorFila, setEstudioPorFila] = useState({});

  useEffect(() => {
    let activo = true;
    verificacionOmeService
      .listarTiposEstudio()
      .then((data) => {
        if (activo) setTiposEstudio(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        // Si fallan los tipos de estudio, la bandeja sigue funcionando; el select
        // queda vacío y "OME OK" avisará que falta elegir el estudio.
        if (activo) setTiposEstudio([]);
      });
    return () => {
      activo = false;
    };
  }, []);

  const handleEstudioChange = (idVerificacionOme, valor) => {
    setEstudioPorFila((prev) => ({ ...prev, [idVerificacionOme]: valor }));
    setErrorResolucion(null);
  };

  const resolverVerificacion = async (idVerificacionOme, omeVerificada) => {
    // Al marcar OME OK es obligatorio elegir el tipo de estudio: viaja al bot para
    // que el paciente no tenga que elegirlo. Al marcar OME no OK no se requiere.
    const idTipoEstudioRaw = estudioPorFila[idVerificacionOme];
    if (omeVerificada && !idTipoEstudioRaw) {
      setErrorResolucion('Elegí el tipo de estudio antes de marcar la OME como verificada.');
      return;
    }

    const idTipoEstudio = omeVerificada ? Number(idTipoEstudioRaw) : null;

    setResolviendoId(idVerificacionOme);
    setErrorResolucion(null);
    try {
      await verificacionOmeService.resolver(idVerificacionOme, omeVerificada, idTipoEstudio);
      // Al resolverse, la fila deja de estar pendiente: refrescamos la lista.
      await refrescar();
    } catch (err) {
      setErrorResolucion(
        'No pudimos guardar la resolución de la OME. Por favor, intentá de nuevo en unos instantes.'
      );
    } finally {
      setResolviendoId(null);
    }
  };

  return (
    <div className="grilla-turnos">
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <AssignmentTurnedIn sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            OME por verificar
          </h2>
          <span className="page-subtitle">
            Afiliados de PAMI pendientes de verificación de OME en el portal
          </span>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left">
              <h3 className="results-title">Pendientes</h3>
            </div>
            <div className="results-info">
              <span className="results-count">{verificaciones.length} pendientes</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={refrescar}
                disabled={loading}
                aria-label="Actualizar lista de OME por verificar"
              >
                <Refresh sx={{ fontSize: 18, mr: 0.5 }} /> Actualizar
              </Button>
            </div>
          </ResultsToolbar>

          {error && (
            <div className="ome-error-message" role="alert">
              {error}
            </div>
          )}

          {errorResolucion && (
            <div className="ome-error-message" role="alert">
              {errorResolucion}
            </div>
          )}

          <div className="table-container table-container-scrollable">
            <DataTable
              className="data-table"
              role="table"
              aria-label="Listado de OME por verificar"
            >
              <thead>
                <tr>
                  <th scope="col">Número de afiliado</th>
                  <th scope="col">Nombre</th>
                  <th scope="col">Apellido</th>
                  <th scope="col">Teléfono</th>
                  <th scope="col">Fecha de alta</th>
                  <th scope="col">Estado del dato</th>
                  <th scope="col">Tipo de estudio</th>
                  <th scope="col">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {verificaciones.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="empty-state">
                      {loading ? 'Cargando...' : 'No hay OME pendientes de verificación.'}
                    </td>
                  </tr>
                ) : (
                  verificaciones.map((v) => (
                    <tr key={v.idVerificacionOme}>
                      <td>{v.numeroAfiliado || '-'}</td>
                      <td>{v.nombre || '-'}</td>
                      <td>{v.apellido || '-'}</td>
                      <td>{v.telefono || '-'}</td>
                      <td>{formatearFecha(v.fechaAlta)}</td>
                      <td>
                        {v.numeroAfiliadoExtraido === false ? (
                          <span
                            className="ome-badge-falta-afiliado"
                            role="status"
                            aria-label="Falta el número de afiliado, la credencial no pudo leerse"
                          >
                            Falta número de afiliado
                          </span>
                        ) : (
                          <span className="ome-badge-ok" aria-label="Datos del afiliado completos">
                            Datos completos
                          </span>
                        )}
                      </td>
                      <td>
                        <label htmlFor={`estudio-${v.idVerificacionOme}`} className="visually-hidden">
                          Tipo de estudio para {v.nombre || 'afiliado'} {v.apellido || ''}
                        </label>
                        <select
                          id={`estudio-${v.idVerificacionOme}`}
                          className="ome-select-estudio"
                          value={estudioPorFila[v.idVerificacionOme] || ''}
                          onChange={(e) => handleEstudioChange(v.idVerificacionOme, e.target.value)}
                          disabled={resolviendoId === v.idVerificacionOme}
                        >
                          <option value="">Elegí un estudio...</option>
                          {tiposEstudio.map((te) => (
                            <option key={te.idTipoEstudio} value={te.idTipoEstudio}>
                              {te.tipoEstudio || te.estudio || `Estudio ${te.idTipoEstudio}`}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div className="ome-acciones" role="group" aria-label={`Resolver OME de ${v.nombre || 'afiliado'} ${v.apellido || ''}`.trim()}>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => resolverVerificacion(v.idVerificacionOme, true)}
                            disabled={resolviendoId === v.idVerificacionOme}
                            aria-label={`Marcar OME verificada de ${v.nombre || 'afiliado'} ${v.apellido || ''}`.trim()}
                          >
                            {resolviendoId === v.idVerificacionOme ? 'Guardando...' : 'OME OK'}
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => resolverVerificacion(v.idVerificacionOme, false)}
                            disabled={resolviendoId === v.idVerificacionOme}
                            aria-label={`Marcar OME no verificada de ${v.nombre || 'afiliado'} ${v.apellido || ''}`.trim()}
                          >
                            {resolviendoId === v.idVerificacionOme ? 'Guardando...' : 'OME no OK'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </div>
        </ResultsCard>
      </div>
    </div>
  );
};

export default BandejaVerificacionOme;
